import classNames from "classnames";
import React, { type Ref, useImperativeHandle, useRef } from "react";
import "./Buttons.css";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

type Props = {
    className?: string;
    children?: React.ReactNode;
    disabled?: boolean;
    icon?: string;
    label?: string;
    showLoadingSpinner?: boolean;
    nowrap?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    iconOnly?: boolean;
} & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, "type">;

function BaseButton(baseClassName?: string) {
    return React.forwardRef((props: Props, ref: Ref<HTMLButtonElement | null>) => {
        const {
            type,
            className,
            children,
            disabled,
            icon,
            label,
            showLoadingSpinner,
            nowrap,
            onClick,
            iconOnly,
        } = props;

        const buttonRef = useRef<HTMLButtonElement>(null);
        useImperativeHandle(ref, () => {
            if (!buttonRef.current) {
                throw new Error("Button ref is null - component not properly mounted");
            }

            return buttonRef.current;
        });

        return (
            <button
                ref={buttonRef}
                type={type || "button"}
                className={classNames(
                    "button-base",
                    baseClassName,
                    {
                        "button-base--is-loading": showLoadingSpinner,
                        "button-base--icon-only": iconOnly,
                    },
                    className,
                )}
                disabled={disabled}
                onClick={(e) => {
                    e.stopPropagation();
                    if (disabled) return;
                    if (showLoadingSpinner) return;
                    if (onClick) {
                        onClick(e);
                    }
                }}
            >
                {showLoadingSpinner && <LoadingSpinner.Medium.AbsoluteCenter />}
                <span
                    className={classNames("button-base__inner", {
                        "button-base__inner--nowrap": nowrap,
                    })}
                >
                    {icon && (
                        <img
                            src={icon}
                            className={classNames(
                                "button-base__icon",
                                `${className}__icon`,
                            )}
                            alt=""
                        />
                    )}
                    {label}
                    {children}
                </span>
            </button>
        );
    });
}

const Button = Object.assign(BaseButton("button-border"), {
    Ghost: BaseButton("button-ghost"),
    Plain: BaseButton("button-plain"),
    Primary: BaseButton("button-primary"),
    PrimaryAttention: BaseButton("button-primary-attention"),
    Secondary: BaseButton("button-secondary"),
    E40: BaseButton("button-e40"),
    E90: BaseButton("button-e90"),
    P50: BaseButton("button-p50"),
    P90: BaseButton("button-p90"),
    N10: BaseButton("button-n10"),
    N90: BaseButton("button-n90"),
});

export default Button;
